import { getDayEffectiveEndPosition } from '@koob/utils';
import { getDayExperience, getDayHotel } from './getDayProducts';

// Pure answers to "where should the map look": the country the trip is in
// when no day is picked, and the spot a picked day sits on.

export interface MapCenter {
  lat: number;
  lng: number;
}

export const DEFAULT_POSITION: MapCenter = { lat: 50.633, lng: 3.066 };

export const hasValidCenter = (center: any): boolean =>
  center?.lat != null &&
  center?.lng != null &&
  Number.isFinite(Number(center.lat)) &&
  Number.isFinite(Number(center.lng));

// The first country of the trip, else the organization's own position, else a
// fixed default.
export function getDefaultMapPosition({
  countries,
  organizationPosition,
}: {
  countries?: any[];
  organizationPosition?: any;
}): MapCenter {
  const defaultCountry =
    countries && countries.length > 0
      ? countries[0]?.position
      : organizationPosition || {};
  const countryLat = defaultCountry?.lat;
  const countryLng = defaultCountry?.lng ?? defaultCountry?.lon;
  const position = {
    lat: parseFloat(countryLat?.toString()),
    lng: parseFloat(countryLng?.toString()),
  };

  return Number.isFinite(position.lat) && Number.isFinite(position.lng)
    ? position
    : DEFAULT_POSITION;
}

const toPoint = (position: any) => ({
  lat: parseFloat(position.lat),
  lon: parseFloat(position.lon),
});

// KD-3548 — the map follows where the day actually ends: a retained
// transfer moves it, and the pin must not stay on the location the
// traveller has left. Falls back to the program's own end position on its
// own, so a day with no transfer centres exactly where it did before.
function getExperienceFocus({
  days,
  dayIndex,
  day,
  experience,
  experiencesMap,
}: {
  days: any[];
  dayIndex: number;
  day: any;
  experience: any;
  experiencesMap: any;
}) {
  const program = experience.programs?.[(day.experienceDayIndex ?? 1) - 1];
  const dayEndPosition = getDayEffectiveEndPosition({
    days,
    dayIndex,
    experiencesMap,
    experience,
    program,
  });

  if (dayEndPosition?.lat != null && dayEndPosition?.lon != null) {
    return toPoint(dayEndPosition);
  }

  if (
    program?.startPosition?.lat != null &&
    program?.startPosition?.lon != null
  ) {
    return toPoint(program.startPosition);
  }

  return null;
}

// A hotel wins over the experience of the same day. `null` when the day has
// no position to show.
export function getDayFocusTarget({
  days,
  dayIndex,
  experiencesMap,
  hotelsMap,
}: {
  days: any[];
  dayIndex: number;
  experiencesMap: any;
  hotelsMap: any;
}) {
  const day = days[dayIndex];
  const experience = getDayExperience(day, experiencesMap);
  const hotel = getDayHotel(day, hotelsMap);

  if (hotel?.lat != null && hotel?.lon != null) {
    return toPoint(hotel);
  }

  return experience
    ? getExperienceFocus({ days, dayIndex, day, experience, experiencesMap })
    : null;
}
